import { apiFetch } from "@/lib/auth-api";

export type RodzajLeku = "OTC" | "RX" | "RX100" | "BD" | "ALL";

export interface DashboardQuery {
	scope?: string;
	/** Ręcznie odznaczone apteki; ten sam zbiór we wszystkich okresach. */
	excludedIdfirms?: string;
	category?: string;
	/** Tylko produkty z flagą kosmetyk w IQVIA CHC (APC mapping). */
	kosmetyk?: boolean;
	/**
	 * Tylko produkty Natubay — producent z kartoteki BLOZ równy dokładnie
	 * wartości Natubay (ta sama reguła co segment „Natubay”). Flaga ORTOGONALNA
	 * do `rodzaj` i `kosmetyk` (przecięcie); pasek ustawia ją wyłącznie sama.
	 */
	natubay?: boolean;
	rodzaj?: RodzajLeku;
	dateFrom?: string;
	dateTo?: string;
}

export interface CentralPricingItem {
	code: string;
	label: string;
	coveragePct: number | null;
	impactPln: number | null;
}

export interface CennikAnomalyProduct {
	bloz07: string;
	name: string;
	opakowanie: string | null;
	dawka: string | null;
	salesQty: number;
	medianSellBrutto: number | null;
	cennikPriceBrutto: number | null;
	deviationPct: number | null;
	upsidePln: number;
}

export interface DashboardCennikAnomaliesResponse {
	products: CennikAnomalyProduct[];
	/** Suma wpływu dla tego cennika (jak wartość w karcie KPI); z PEŁNEGO zbioru, niezależnie od `limit`. */
	totalUpsidePln: number;
	/** Wszystkie pozycje poniżej cennika — mianownik „N z total”. */
	totalRows: number;
	limit: number;
	/** `products.length < totalRows` — zbiór nie zmieścił się w odpowiedzi. */
	truncated: boolean;
	meta: ResponseMeta;
}

export interface CheapestBasketProduct {
	bloz07: string;
	name: string;
	opakowanie: string | null;
	dawka: string | null;
	medianPriceNet: number;
}

/** Top 5 wg sprzedaży — kandydaci koszyka z flagą FV w okresie. */
export interface CheapestBasketSourceProduct {
	bloz07: string;
	name: string;
	opakowanie: string | null;
	dawka: string | null;
	salesQty: number;
	includedInBasket: boolean;
}

export interface CheapestBasketItem {
	rank: number;
	supplierKey: string;
	supplierName: string;
	supplierNip: string | null;
	basketTotal: number;
	avgBasketTotal: number;
	savingsVsAvg: number;
	productsCount: number;
	items: CheapestBasketProduct[];
}

export interface ResponseMeta {
	scope: string;
	visibleIdfirmsCount: number | null;
	dateFrom: string;
	dateTo: string;
	computedAt: string;
	dbDurationMs: number;
	/** Ostatni KOMPLETNY dzień (D-1) wspólny dla źródeł tej odpowiedzi. */
	dataAsOf: string;
	/** finished_at ostatniego udanego refreshu źródła; null = brak historii. */
	sourceRefreshedAt: string | null;
	/** Źródło ograniczające dataAsOf (endpoint hybrydowy → wolniejsze). */
	dataAsOfSource: string;
}

export interface DashboardPharmacyOptionsResponse {
	pharmacies: Array<{ idfirm: number; name: string; salesNetPln: number | null }>;
	dateFrom: string;
	dateTo: string;
	/** Brak kompletnego dnia bieżącego miesiąca = null, nie sprzedaż zero. */
	available: boolean;
	meta: ResponseMeta;
}

/** Lista przed odznaczeniem: własny bieżący miesiąc, tylko zakres i uprawnienia. */
export function fetchDashboardPharmacyOptions(
	scope: string | undefined,
	signal?: AbortSignal,
): Promise<DashboardPharmacyOptionsResponse> {
	const qs = new URLSearchParams();
	if (scope) qs.set("scope", scope);
	return apiFetch<DashboardPharmacyOptionsResponse>(
		`/reports/dashboard/pharmacy-options?${qs.toString()}`,
		{ signal },
	);
}

/** Kody błędów granic dashboardu (422/503) — patrz backend shared.rs. */
export const DASHBOARD_RANGE_CODE = "DATE_RANGE_OUT_OF_BOUNDS";
export const DATA_PREPARING_CODE = "DATA_PREPARING";
/** MV spopulowane, ale bez historii refreshu — świeżość nieznana (fail-closed). */
export const DATA_FRESHNESS_UNKNOWN_CODE = "DATA_FRESHNESS_UNKNOWN";
/** 400: `scope` nieparsowalny/nieznany — backend NIE spada już cicho do całej widoczności. */
export const INVALID_SCOPE_CODE = "INVALID_SCOPE";

export interface DateRangeBounds {
	minDate: string;
	maxDate: string;
}

export interface CategoryTiles {
	salesNetPln: number;
	qty: number;
	marginPln: number;
	marginPct: number | null;
}

export interface CategoryTopProduct {
	bloz07: string;
	name: string;
	opakowanie: string | null;
	dawka: string | null;
	qty: number;
	salesNetPln: number;
	marginPln: number;
	marginPct: number | null;
	primaryCategoryCode: string | null;
	primaryCategoryLabel: string | null;
	/** Udział BLOZ w JEGO kategorii primary (%); null = „Bez kategorii”. */
	categorySharePct: number | null;
}

export interface DashboardCategoryProductsResponse {
	tiles: CategoryTiles;
	topProducts: CategoryTopProduct[];
	meta: ResponseMeta;
}

/** KPI węzła w jednym DOSTĘPNYM okresie (brak wpisu = brak sprzedaży ≠ 0). */
export interface CategoryNodePeriodTotals extends OverviewTotals {
	period: OverviewPeriodToken;
}

/**
 * Węzeł drzewa kategorii. KPI = suma przypisań primary CAŁEGO poddrzewa
 * (rollup po `parentCode`), każdy BLOZ najwyżej raz. `code = null` ⇒ jawny
 * root „Bez kategorii” (bez sztucznego rodzica).
 */
export interface CategoryHierarchyNode {
	code: string | null;
	label: string | null;
	parentCode: string | null;
	level: number;
	/** Dzieci Z DANYMI w tej odpowiedzi — klik: rozwiń vs otwórz szczegół. */
	hasChildren: boolean;
	periods: CategoryNodePeriodTotals[];
}

export interface DashboardCategoryHierarchyResponse {
	/** Zakresy i dostępność okresów — te same reguły co `/overview`. */
	periods: SegmentPeriodInfo[];
	/** Kafle okresu bieżącego z PEŁNEJ filtrowanej populacji. */
	tiles: CategoryTiles;
	/** Wszystkie węzły z danymi, malejąco po bieżącej sprzedaży netto. */
	nodes: CategoryHierarchyNode[];
	meta: ResponseMeta;
}

/**
 * Punkt szeregu dziennego szczegółu: (dzień × kategoria primary).
 * Dni BEZ sprzedaży NIE mają punktu — brak ≠ zero; oś dat buduje frontend
 * z `meta.dateFrom/dateTo`. Wartości zachowują znak (zwroty ⇒ ujemne).
 */
export interface CategoryDetailPoint {
	saleDate: string;
	/** null ⇒ „Bez kategorii” (token `__none` w URL i w API). */
	categoryCode: string | null;
	qty: number;
	salesNetPln: number;
	marginPln: number;
	marginPct: number | null;
}

/** Total kategorii = suma jej pełnej serii (i wiersz rankingu przy tych filtrach). */
export interface CategoryDetailTotal {
	categoryCode: string | null;
	categoryLabel: string | null;
	qty: number;
	salesNetPln: number;
	marginPln: number;
	marginPct: number | null;
}

export interface DashboardCategoryDetailResponse {
	series: CategoryDetailPoint[];
	totals: CategoryDetailTotal[];
	meta: ResponseMeta;
}

/** Okresy dziennego szczegółu SKU: trzy z Przeglądu plus YTD i jego rok wcześniej. */
export type SkuDailyPeriodToken = OverviewPeriodToken | "ytd" | "ytd_previous";
/** Etykiety biznesowe porównań: miesiąc do miesiąca, rok do roku, rok do daty. */
export type SkuDailyComparisonKind = "MTM" | "YOY" | "YTD";

/** Punkt dzienny jednego SKU. Dni bez sprzedaży NIE mają punktu (brak ≠ 0). */
export interface SkuDailyPoint {
	saleDate: string;
	qty: number;
	salesNetPln: number;
	marginPln: number;
	marginPct: number | null;
}

/** Okres z FAKTYCZNIE użytymi datami; `totals: null` ⇔ `available: false`. */
export interface SkuDailyPeriod {
	period: SkuDailyPeriodToken;
	dateFrom: string;
	dateTo: string;
	days: number;
	/** false = okno MV nie pokrywa okresu w całości — bez liczb z kawałka. */
	available: boolean;
	totals: OverviewTotals | null;
}

export interface SkuDailyComparison {
	kind: SkuDailyComparisonKind;
	/** Oba okresy dostępne. */
	available: boolean;
	latest: SkuDailyPeriod;
	baseline: SkuDailyPeriod;
	delta: OverviewDelta;
}

/**
 * Dzienny szczegół SKU: szereg dzienny bieżącego zakresu + porównania
 * MTM (`previous_period`), YOY (`yoy_period`) i YTD (1 stycznia → dateTo
 * vs to samo okno rok wcześniej). Zawsze FAKT — repo nie ma źródła prognozy
 * per SKU, więc `valueKind` jest stały i jawny.
 */
export interface DashboardSkuDailyResponse {
	bloz07: string;
	name: string | null;
	/** false = towar poza populacją listy pod tymi filtrami (szereg pusty). */
	inPopulation: boolean;
	valueKind: "FAKT";
	unitQty: string;
	unitPln: string;
	series: SkuDailyPoint[];
	current: SkuDailyPeriod;
	comparisons: SkuDailyComparison[];
	meta: ResponseMeta;
}

/** Jawny, stabilny token „Bez kategorii” — wspólny kontrakt URL ↔ backend. */
export const NO_CATEGORY_TOKEN = "__none";
/**
 * Ziarno szeregu szczegółu. `day` = szereg dzienny dla zakresu z paska
 * filtrów; `month` = widok ROCZNY: 12 miesięcy roku kalendarzowego z `dateTo`,
 * okno wyznacza backend (rok docięty do okna MV i ostatniego kompletnego
 * dnia), a `saleDate` punktu to pierwszy dzień miesiąca.
 */
export type DetailGrain = "day" | "month";
/** Cap selekcji szczegółu (parytet z backendem: 422 powyżej). */
export const MAX_DETAIL_CATEGORIES = 5;
/** Kod 422 przekroczonego capu selekcji. */
export const DETAIL_TOO_MANY_CODE = "DETAIL_TOO_MANY_CATEGORIES";

export interface CategoryProductExportRow {
	bloz07: string;
	name: string;
	opakowanie: string | null;
	dawka: string | null;
	substancja: string | null;
	producent: string | null;
	primaryCategoryCode: string | null;
	primaryCategoryLabel: string | null;
	qty: number;
	salesNetPln: number;
	marginPln: number;
	marginPct: number | null;
	categorySharePct: number | null;
	/** Wyłącznie potwierdzone atrybuty (gałąź „30 ATRYBUT”, verified=true). */
	attributes: string | null;
	/**
	 * `false` = towar wszedł do listy WYŁĄCZNIE ze snapshotu magazynu
	 * (`universe: "stock"`) i w okresie nie miał żadnej linii sprzedaży —
	 * jego sprzedaż/ilość/marża to faktyczne zera. Opcjonalne, bo starsze
	 * odpowiedzi i fixture'y go nie mają (wtedy = sprzedany).
	 */
	soldInPeriod?: boolean;
	/**
	 * Magazyn z dnia `stockAsOf` odpowiedzi, po aptekach scope ∩ visible.
	 * `null` znaczy trzy różne rzeczy — rozstrzyga `stockStatus` odpowiedzi:
	 * nie pytano / brak snapshotu / sprzedany towar bez pozycji w snapshocie.
	 * Zero i stan ujemny przychodzą jako liczba. Pola opcjonalne, bo starsze
	 * odpowiedzi (i fixture'y) ich nie mają.
	 */
	stockQty?: number | null;
	/** Wartość zapasu w CENIE ZAKUPU NETTO (`value_purchase_net`). */
	stockValueNet?: number | null;
	/** Apteki ze stanem > 0 w snapshocie. */
	stockPharmacyCount?: number | null;
	/** `MAX(last_purchase_date)` ze snapshotu (ISO). */
	lastPurchaseDate?: string | null;
}

/** Status kolumn magazynowych — `null` w wierszu sam nic nie mówi. */
export type StockStatus = "notRequested" | "ok" | "unavailable";

/**
 * Mianownik udziału „w kategorii primary” dla kategorii obecnych w populacji
 * listy: PEŁNA kategoria (przed multiselectem, frazą i filtrami kartoteki)
 * przy tym samym scope, okresie i segmencie. `stockValueNet` to zapas
 * SPRZEDANYCH SKU kategorii, nie cały magazyn kategorii.
 */
export interface CategoryTotalRow {
	categoryCode: string;
	categoryLabel: string | null;
	salesNetPln: number;
	marginPln: number;
	stockValueNet: number | null;
}

/**
 * Populacja listy SKU — NIEZALEŻNA od miary. `sales` = towary sprzedane w
 * okresie (domyślnie, jak dotąd); `stock` = sprzedane w okresie LUB na stanie
 * (≠ 0) w bieżącym snapshocie magazynu w aptekach zakresu. Towar tylko ze
 * snapshotu ma `soldInPeriod: false` i faktyczne zera sprzedaży.
 */
export type SkuUniverse = "sales" | "stock";

export interface DashboardCategoryProductsExportResponse {
	rows: CategoryProductExportRow[];
	stockStatus?: StockStatus;
	stockAsOf?: string | null;
	pricelistAsOf?: string | null;
	pricelistVersion?: string | null;
	/** Populacja odpowiedzi; brak = `sales` (starsze odpowiedzi i fixture'y). */
	skuUniverse?: SkuUniverse;
	meta: ResponseMeta;
}

/** Domyślny rozmiar strony listy SKU (parytet z backendem). */
export const PRODUCTS_PAGE_LIMIT = 100;
/** Kod 422 niepoprawnego okna strony (parytet z backendem). */
export const PRODUCTS_PAGE_INVALID_CODE = "PRODUCTS_PAGE_INVALID_RANGE";

/**
 * Jedna strona listy SKU. `totalRows` to PEŁNA liczba wierszy pod aktywnymi
 * filtrami — ekran zna rozmiar zbioru, nie pobierając go.
 */
export interface DashboardCategoryProductsPageResponse {
	rows: CategoryProductExportRow[];
	totalRows: number;
	baselineRows?: number;
	/** Populacja odpowiedzi; brak = `sales`. */
	skuUniverse?: SkuUniverse;
	/** Suma sprzedaży netto tej samej, kartotekowo przefiltrowanej populacji. */
	populationSalesNetPln: number;
	/** Suma marży PLN tej samej populacji (mianownik udziału w marży — propozycja). */
	populationMarginPln?: number;
	/** Zapas populacji; `null` = nie pytano albo brak snapshotu (nigdy 0 udające brak). */
	populationStockValueNet?: number | null;
	populationStockQty?: number | null;
	stockStatus?: StockStatus;
	/** Dzień snapshotu, do którego przypinane są kolejne strony i eksport. */
	stockAsOf?: string | null;
	pricelistAsOf?: string | null;
	pricelistVersion?: string | null;
	categoryTotals?: CategoryTotalRow[];
	hasMore: boolean;
	nextOffset: number | null;
	limit: number;
	offset: number;
	meta: ResponseMeta;
}

/**
 * Kafle „Magazyn na dziś”. Źródło: `inventory_daily_snapshot` przy
 * `MAX(snapshot_date)` — stan BIEŻĄCY, nie cofany zakresem dat dashboardu.
 * Baza ceny jest jawna: `valuePurchase*` to CENA ZAKUPU z partii kzak.
 */
export interface StockKpiBlock {
	valuePurchaseNet: number;
	valuePurchaseGross: number;
	qty: number;
	positionCount: number;
	blozCount: number;
	/** Apteki ZE STANEM w snapshocie (≠ liczba aptek w zakresie). */
	pharmacyCount: number;
	batchCount: number;
	/** Pozycje z najwcześniejszym terminem ważności przed dniem snapshotu. */
	expiredPositionCount: number;
	/** `stan / (sprzedaż_90d / 90)`; null przy niedodatniej sprzedaży w oknie. */
	coverageDays: number | null;
	salesQtyWindow: number;
}

/** Struktura wartości magazynu wg kategorii primary; null ⇒ „Bez kategorii”. */
export interface StockCategoryRow {
	categoryCode: string | null;
	categoryLabel: string | null;
	valuePurchaseNet: number;
	qty: number;
	positionCount: number;
}

/** Okno sprzedaży użyte WYŁĄCZNIE do rotacji — rozdzielone od „stanu na”. */
export interface SalesWindow {
	dateFrom: string;
	dateTo: string;
	days: number;
	source: string;
}

export interface DashboardStockKpiResponse {
	kpi: StockKpiBlock;
	/** PEŁNA struktura, malejąco po wartości (bounded liczbą kategorii). */
	categories: StockCategoryRow[];
	salesWindow: SalesWindow;
	meta: ResponseMeta;
}

/** Kod 503 — brak snapshotu magazynu (fail-closed, zamiast zerowych kafli). */
export const STOCK_SNAPSHOT_MISSING_CODE = "STOCK_SNAPSHOT_MISSING";
/** Kod 422 — `dataAsOf` z deep linku nie jest bieżącym dniem snapshotu. */
export const STOCK_SNAPSHOT_STALE_CODE = "STOCK_SNAPSHOT_STALE";

/**
 * Metryki magazynu, które da się JEDNOZNACZNIE rozłożyć na pozycje snapshotu
 * — tylko one dostają CTA na kaflu. Token jest stabilnym enumem w URL-u
 * (NIE polską etykietą: copy się zmienia, deep linki nie mogą).
 *
 * Poza listą świadomie zostają: „Apteki ze stanem” (COUNT DISTINCT — inny
 * grain niż wiersz), „Pokrycie (dni)” (iloraz, nie suma) oraz metryki bez
 * potwierdzonego źródła (zaleganie, horyzont przeterminowania, nadmiar, po
 * terminie płatności).
 */
export const STOCK_DETAIL_METRICS = [
	"value_purchase_net",
	"value_purchase_gross",
	"qty",
	"positions",
	"expired_positions",
] as const;

export type StockDetailMetric = (typeof STOCK_DETAIL_METRICS)[number];

/** Lustro whitelisty `SORTABLE` z `stock_detail.rs` — spoza niej API zwraca 422. */
export const STOCK_DETAIL_SORT_COLUMNS = [
	"metric",
	"qty",
	"pharmacy",
	"bloz07",
	"product",
	"category",
	"expiry",
] as const;

export type StockDetailSortColumn = (typeof STOCK_DETAIL_SORT_COLUMNS)[number];

export function asStockDetailSortColumn(
	value: string | undefined,
): StockDetailSortColumn | undefined {
	return STOCK_DETAIL_SORT_COLUMNS.includes(value as StockDetailSortColumn)
		? (value as StockDetailSortColumn)
		: undefined;
}

export function isStockDetailMetric(value: unknown): value is StockDetailMetric {
	return STOCK_DETAIL_METRICS.includes(value as StockDetailMetric);
}

/**
 * Wiersz szczegółu = jedna POZYCJA snapshotu (BLOZ × apteka).
 *
 * Pola mają DWA źródła: `form`…`lastPurchaseDate` pochodzą ze SNAPSHOTU z dnia
 * `meta.dataAsOf`, a `producer`/`substance`/`packaging` to BIEŻĄCE metadane
 * katalogu BLOZ (nie są snapshotowane). Brak wartości opcjonalnej ⇒ `null`.
 */
export interface StockDetailRow {
	idfirm: number;
	pharmacyName: string | null;
	bloz07: string;
	productName: string | null;
	/** Postać — snapshot. */
	form: string | null;
	/** Dawka — snapshot. */
	dosage: string | null;
	categoryCode: string | null;
	categoryLabel: string | null;
	/** `total_stock` — niezależnie od metryki; znak zachowany. */
	qty: number;
	valuePurchaseNet: number;
	valuePurchaseGross: number;
	/** `value_sell_price` — kolumna NOT NULL; `0` = brak ustalonej ceny. */
	valueSellPrice: number;
	/** Mediana ceny sprzedaży — brak ⇒ null. */
	medianSellPrice: number | null;
	batchCount: number;
	/** Brak daty ważności ⇒ null, NIGDY 0. */
	earliestExpiry: string | null;
	/** Jedyna potwierdzona „data zakupu” na tym grainie; brak ⇒ null. */
	lastPurchaseDate: string | null;
	/** Producent — BIEŻĄCE metadane katalogowe BLOZ. */
	producer: string | null;
	/** Substancja czynna — BIEŻĄCE metadane katalogowe BLOZ. */
	substance: string | null;
	/** Opakowanie — BIEŻĄCE metadane katalogowe BLOZ. */
	packaging: string | null;
	/** Wkład wiersza w kafel (metryki licznikowe ⇒ 1). */
	metricValue: number;
}

/** Definicje metryki z API — jedno źródło dla nagłówka strony i arkusza `Kryteria`. */
export interface StockDetailMetricInfo {
	key: StockDetailMetric;
	label: string;
	definition: string;
	unit: string;
	valueHeader: string;
}

export interface DashboardStockDetailResponse {
	metric: StockDetailMetricInfo;
	/** SUM(metricValue) po PEŁNYM zbiorze po filtrach = wartość kafla. */
	total: number;
	/** COUNT(*) po pełnym zbiorze — liczba pozycji wyjaśniających kafel. */
	rowCount: number;
	rows: StockDetailRow[];
	/** `rows` nie objęło całego zbioru (sufit limitu) — UI musi ostrzec. */
	truncated: boolean;
	sort: { sortBy: string; sortDir: string };
	q: string | null;
	meta: ResponseMeta;
}

export interface StockDetailParams {
	metric: StockDetailMetric;
	scope?: string;
	excludedIdfirms?: string;
	category?: string;
	kosmetyk?: boolean;
	natubay?: boolean;
	rodzaj?: RodzajLeku;
	/** Dzień snapshotu przeniesiony z kafla — backend go WERYFIKUJE, nie ufa. */
	dataAsOf?: string;
	q?: string;
	sortBy?: StockDetailSortColumn;
	sortDir?: "asc" | "desc";
	limit?: number;
	offset?: number;
}

export function buildStockDetailQS(params: StockDetailParams): string {
	const qs = new URLSearchParams();
	qs.set("metric", params.metric);
	if (params.scope) qs.set("scope", params.scope);
	if (params.excludedIdfirms) qs.set("excludedIdfirms", params.excludedIdfirms);
	if (params.category) qs.set("category", params.category);
	if (params.kosmetyk) qs.set("kosmetyk", "true");
	if (params.natubay) qs.set("natubay", "true");
	if (params.rodzaj && params.rodzaj !== "ALL") qs.set("rodzaj", params.rodzaj);
	if (params.dataAsOf) qs.set("dataAsOf", params.dataAsOf);
	if (params.q) qs.set("q", params.q);
	if (params.sortBy) qs.set("sortBy", params.sortBy);
	if (params.sortDir) qs.set("sortDir", params.sortDir);
	if (params.limit != null) qs.set("limit", String(params.limit));
	if (params.offset) qs.set("offset", String(params.offset));
	return `?${qs.toString()}`;
}

/**
 * Szczegół kafla magazynu — JEDEN endpoint dla wszystkich metryk. Zakres dat
 * dashboardu NIE jest wysyłany: stan to bieżący snapshot, a `dataAsOf` służy
 * wyłącznie do wykrycia nieaktualnego deep linku.
 */
export function fetchDashboardStockDetail(
	params: StockDetailParams,
	signal?: AbortSignal,
): Promise<DashboardStockDetailResponse> {
	return apiFetch<DashboardStockDetailResponse>(
		`/reports/dashboard/stock-detail${buildStockDetailQS(params)}`,
		{ signal },
	);
}

export interface DashboardCentralPricingResponse {
	centralPricingImpact: CentralPricingItem[];
	/** Wszystkie cenniki z wpływem w zakresie — mianownik „N z total”. */
	totalRows: number;
	limit: number;
	/** `centralPricingImpact.length < totalRows` — zbiór nie zmieścił się w odpowiedzi. */
	truncated: boolean;
	meta: ResponseMeta;
}

/** Domyślny TOP na ekranie — backendowy `DEFAULT_LIMIT` obu endpointów cennikowych. */
export const CENTRAL_PRICING_DEFAULT_LIMIT = 10;
/** Sufit eksportu pełnego zbioru — backendowy `MAX_LIMIT`. */
export const CENTRAL_PRICING_EXPORT_LIMIT = 50_000;

export interface DashboardCheapestBasketResponse {
	baskets: CheapestBasketItem[];
	basketProducts: CheapestBasketSourceProduct[];
	eligibleBasketSize: number;
	meta: ResponseMeta;
}

export interface SeasonalForecast {
	valuePln: number;
	ciLowPln: number;
	ciHighPln: number;
	baselineAvg3mPln: number;
	seasonalIndex: number;
	stddev12mPln: number;
}

export interface DashboardForecastResponse extends SeasonalForecast {
	turnover: SeasonalForecast;
	excludedRx: boolean;
	meta: ResponseMeta;
}

// ---------------------------------------------------------------------------
// Przegląd — sprzedaż i marża (slice 7)
// ---------------------------------------------------------------------------

/** Token okresu — ten sam string w API, w wykresie i w kolumnie „Okres” XLSX. */
export type OverviewPeriodToken = "current" | "previous" | "yoy";

/** Punkt szeregu dziennego. Dni bez sprzedaży NIE mają punktu (brak ≠ 0). */
export interface OverviewPoint {
	saleDate: string;
	salesNetPln: number;
	qty: number;
	marginPln: number;
	marginPct: number | null;
}

export interface OverviewTotals {
	salesNetPln: number;
	qty: number;
	marginPln: number;
	/** `marginPln / salesNetPln * 100` przy dodatnim mianowniku, inaczej null. */
	marginPct: number | null;
}

export interface OverviewPeriod {
	period: OverviewPeriodToken;
	/** FAKTYCZNIE użyty zakres (po przesunięciu i domknięciu 29 lutego). */
	dateFrom: string;
	dateTo: string;
	days: number;
	/** false = źródło nie pokrywa całego okresu; totals/series są puste. */
	available: boolean;
	totals: OverviewTotals | null;
	series: OverviewPoint[];
}

/** Procenty tylko przy dodatnim mianowniku; marża % w PUNKTACH procentowych. */
export interface OverviewDelta {
	salesNetPct: number | null;
	qtyPct: number | null;
	marginPlnPct: number | null;
	marginPctPoints: number | null;
	/** Tylko porównanie w zakresie: zmiana ilości pacjentów. */
	patientCountPct?: number | null;
}

export interface DashboardOverviewResponse {
	current: OverviewPeriod;
	previous: OverviewPeriod;
	yoy: OverviewPeriod;
	deltas: { previous: OverviewDelta; yoy: OverviewDelta };
	meta: ResponseMeta;
}

// ---------------------------------------------------------------------------
// Trend sprzedaży i marży — ostatnie 12 miesięcy
// ---------------------------------------------------------------------------

/**
 * Jeden punkt tygodniowy trendu. `rangeFrom`/`rangeTo` to FAKTYCZNIE pokryty
 * zakres — skrajne tygodnie okna bywają niepełne i tooltip musi to pokazać.
 */
export interface TrendPoint {
	weekStart: string;
	rangeFrom: string;
	rangeTo: string;
	/** false = tydzień przycięty przez granicę okna trendu. */
	complete: boolean;
	salesNetPln: number;
	qty: number;
	marginPln: number;
	marginPct: number | null;
}

export interface DashboardTrendResponse {
	title: string;
	windowNote: string;
	/** Okno TRENDU — niezależne od zakresu dat z paska filtrów. */
	windowFrom: string;
	windowTo: string;
	points: TrendPoint[];
	meta: ResponseMeta;
}

// ---------------------------------------------------------------------------
// Przegląd — segmenty
// ---------------------------------------------------------------------------

/**
 * Stabilne tokeny grup zarobku (SQL == JSON == kolumna XLSX). Te same grupy
 * co warstwy kalkulatora marży: Receptura i Programy partnerskie mają
 * pierwszeństwo przed resztą.
 */
export type SegmentToken =
	| "RECEPTURA"
	| "PARTNER"
	| "NATUBAY"
	| "COSMETICS"
	| "RX"
	| "RX100"
	| "OTC"
	| "UNKNOWN";

/** Kolejność ROZSTRZYGANIA — pierwszy pasujący bucket wygrywa. */
export const SEGMENT_PRIORITY: SegmentToken[] = [
	"RECEPTURA",
	"PARTNER",
	"NATUBAY",
	"COSMETICS",
	"RX",
	"RX100",
	"OTC",
	"UNKNOWN",
];

export interface SegmentPeriodInfo {
	period: OverviewPeriodToken;
	/** FAKTYCZNIE użyty zakres (po przesunięciu i domknięciu 29 lutego). */
	dateFrom: string;
	dateTo: string;
	days: number;
	/** false = źródło nie pokrywa całego okresu; segmenty nie mają jego wiersza. */
	available: boolean;
}

/** Totale jednego segmentu w jednym okresie. Okres niedostępny nie ma wpisu. */
export interface SegmentPeriodTotals extends OverviewTotals {
	period: OverviewPeriodToken;
}

/**
 * Totale segmentu w okresie PLUS wynik kasowy po korektach zaksięgowanych.
 * Dla grup innych niż PARTNER `correctionsPln = 0` i adjusted = raw — jedna
 * arytmetyka dla każdego wiersza: marża handlowa + korekty = po korektach.
 */
export interface SegmentPeriodResult extends SegmentPeriodTotals {
	/** Korekty partnerskie zaksięgowane w TYM okresie (PLN, dodatnie = odzysk). */
	correctionsPln: number;
	/** `marginPln + correctionsPln`. */
	adjustedMarginPln: number;
	/** `adjustedMarginPln / salesNetPln × 100` tylko przy DODATNIEJ sprzedaży. */
	adjustedMarginPct: number | null;
}

/** Delta wyniku po korektach (adjusted vs adjusted); baza ≤ 0 → null. */
export interface AdjustedDelta {
	adjustedMarginPlnPct: number | null;
	adjustedMarginPctPoints: number | null;
}

/** Kontrola sum jednego DOSTĘPNEGO okresu: Σ marża grup + korekty = po korektach. */
export interface SegmentPeriodSummary {
	period: OverviewPeriodToken;
	totalMarginPln: number;
	partnerCorrectionsPln: number;
	totalAdjustedMarginPln: number;
}

export interface SegmentBlock {
	segment: SegmentToken;
	/** Miejsce w kolejności rozstrzygania (1 = sprawdzany pierwszy). */
	priority: number;
	label: string;
	definition: string;
	periods: SegmentPeriodResult[];
	/** Udział w sprzedaży netto okresu bieżącego; null = mianownik ≤ 0. */
	sharePct: number | null;
	/** Delty marży HANDLOWEJ (raw vs raw) — jak w Przeglądzie. */
	deltas: { previous: OverviewDelta; yoy: OverviewDelta };
	/** Delty wyniku po korektach; różne od `deltas` tylko dla PARTNER. */
	adjustedDeltas: { previous: AdjustedDelta; yoy: AdjustedDelta };
}

export interface DashboardSegmentsResponse {
	periods: SegmentPeriodInfo[];
	/** Wszystkie buckety w kolejności priorytetu — także te z zerem. */
	segments: SegmentBlock[];
	/** Mianownik udziałów = suma sprzedaży netto wszystkich segmentów (bieżący). */
	currentSalesNetPln: number;
	/**
	 * Korekty partnerskie zaksięgowane w okresie bieżącym (PLN, dodatnie =
	 * odzysk) = `periodTotals[current].partnerCorrectionsPln`. POZA sumą
	 * marży handlowej i poza mianownikiem udziałów.
	 */
	partnerCorrectionsPln: number;
	/** Kontrola sum per DOSTĘPNY okres: Σ marża grup + korekty = po korektach. */
	periodTotals: SegmentPeriodSummary[];
	partnerCorrectionsNote: string;
	calculatorParityNote: string;
	cosmeticsPriorityNote: string;
	natubayNote: string;
	/** Gdzie w podziale siedzą pozycje bez kodu BLOZ (UX-08). */
	noBlozNote: string;
	meta: ResponseMeta;
}

// ---------------------------------------------------------------------------
// Porównanie w ramach zakresu (scope-breakdown)
// ---------------------------------------------------------------------------

/** Wymiar podziału — tylko potwierdzone źródła kanoniczne (patrz backend). */
export type BreakdownDimension = "kr" | "spolka" | "region" | "apteka";

/** 400: wymiar niedozwolony dla tego rodzaju scope. */
export const INVALID_BREAKDOWN_CODE = "INVALID_BREAKDOWN";
/** Klucz jednostki „Bez przypisania” (NULL-owy KR / NIP / region). */
export const BREAKDOWN_NONE_KEY = "__none";

export interface BreakdownPeriodTotals extends OverviewTotals {
	period: OverviewPeriodToken;
	/** Suma dziennych unikalnych nrkln; null przy filtrze asortymentu. */
	patientCount: number | null;
}

export interface BreakdownUnit {
	key: string;
	label: string;
	/** Apteki z jakąkolwiek sprzedażą w tej jednostce. */
	pharmacies: number;
	/** `current` + dostępne `previous` / `yoy`. */
	periods: BreakdownPeriodTotals[];
	/** Udział w bieżącej sprzedaży całego zakresu; null = mianownik ≤ 0. */
	sharePct: number | null;
	/** Zmiana vs poprzedni równy okres i vs rok wcześniej. */
	deltas: { previous: OverviewDelta; yoy: OverviewDelta };
	/**
	 * Tylko wymiar `apteka`: wartość magazynu netto (cena ZAKUPU, ta sama
	 * formuła co kafle „Magazyn na dziś”) w dniu `stockSnapshotDate`;
	 * null = apteka bez pozycji w snapshocie albo brak snapshotu.
	 */
	stockValueNetPln: number | null;
}

export interface DashboardScopeBreakdownResponse {
	dimension: BreakdownDimension;
	dimensionLabel: string;
	/** Wymiary dozwolone dla bieżącego scope; pierwszy = domyślny. */
	availableDimensions: { dimension: BreakdownDimension; label: string }[];
	periods: SegmentPeriodInfo[];
	/** WSZYSTKIE jednostki malejąco po bieżącej sprzedaży netto; „Bez przypisania” zawsze na końcu. */
	units: BreakdownUnit[];
	/** == units.length; serwer nie tnie populacji (bez „Pozostałe”). */
	totalUnits: number;
	/** Mianownik udziałów — równy totalowi `/overview`. */
	currentSalesNetPln: number;
	/**
	 * Dzień snapshotu magazynu (tylko `apteka`): najpóźniejszy
	 * `snapshot_date <= dateTo + 1` (snapshot z 01:00 dnia X = stan na koniec
	 * X-1), w oknie 30 dni. null = inny wymiar albo brak snapshotu.
	 */
	stockSnapshotDate: string | null;
	meta: ResponseMeta;
}

/** Starszy backend lub cache sprzed wdrożenia YoY i pacjentów. */
export function isLegacyScopeBreakdown(data: DashboardScopeBreakdownResponse | undefined): boolean {
	return (
		!!data &&
		(!data.periods.some((p) => p.period === "yoy") ||
			data.units.some((u) => !u.deltas?.previous || !u.deltas?.yoy))
	);
}

/** Wspólny format dla karty i XLSX; brakujących pomiarów nie zastępujemy zerami. */
export function normalizeScopeBreakdown(
	data: DashboardScopeBreakdownResponse,
): DashboardScopeBreakdownResponse {
	if (!isLegacyScopeBreakdown(data)) return data;
	const unavailable: OverviewDelta = {
		salesNetPct: null,
		qtyPct: null,
		marginPlnPct: null,
		marginPctPoints: null,
		patientCountPct: null,
	};
	return {
		...data,
		units: data.units.map((unit) => ({
			...unit,
			periods: unit.periods.map((period) => ({
				...period,
				patientCount: period.patientCount ?? null,
			})),
			deltas: {
				previous:
					unit.deltas?.previous ??
					(unit as BreakdownUnit & { delta?: OverviewDelta }).delta ??
					unavailable,
				yoy: unit.deltas?.yoy ?? unavailable,
			},
		})),
	};
}

// ---------------------------------------------------------------------------
// Źródła ceny — bieżące pokrycie cennikami
// ---------------------------------------------------------------------------

/** Stabilny token bucketu „reszta” — jedyny kod źródła znany frontendowi. */
export const NO_ACTIVE_SOURCE_CODE = "NO_ACTIVE_CENTRAL_SOURCE";

/**
 * Jeden kafel pokrycia. `sourceCode`/`label`/`badgeColor` przychodzą z
 * REJESTRU źródeł — front nie zna z góry żadnego kodu (poza bucketem
 * domykającym), więc nowe źródło pojawia się bez zmiany kodu.
 */
export interface PriceSourceBucket {
	sourceCode: string;
	label: string;
	badgeColor: string;
	/** Kolejność z rejestru; NIE rozstrzyga konfliktu źródeł (o tym decyduje cena). */
	priority: number;
	isNoSource: boolean;
	/**
	 * Okres niedostępny nie ma wpisu (brak ≠ zero). Kafel bez korekt
	 * partnerskich ma `correctionsPln = 0`, więc adjusted == raw.
	 */
	periods: SegmentPeriodResult[];
	/** Udział w sprzedaży netto okresu bieżącego; null = mianownik ≤ 0. */
	sharePct: number | null;
	deltas: { previous: OverviewDelta; yoy: OverviewDelta };
}

/**
 * Cennik centralny JEDNEGO segmentu (`COSMETICS` = Kosmetyki, `RX100` =
 * RX100 — NOREF, `OTC`): te same buckety co macierz główna, populacja
 * zawężona do segmentu tym samym rozłącznym podziałem co `/segments`
 * (kosmetyk o rodzaju OTC/RX100 liczy się raz, w Kosmetykach). `sharePct`
 * bucketu liczy się WZGLĘDEM segmentu; `currentSalesNetPln` == kafel
 * segmentu w `/segments`. Leków refundowanych (`RX` = RX REF., cena
 * urzędowa) backend tu nie oddaje.
 */
export interface PriceSourceSegmentBlock {
	segment: Extract<SegmentToken, "COSMETICS" | "RX100" | "OTC">;
	priority: number;
	label: string;
	definition: string;
	currentSalesNetPln: number;
	buckets: PriceSourceBucket[];
}

export interface DashboardPriceSourceResponse {
	periods: SegmentPeriodInfo[];
	/** Wszystkie buckety w kolejności rejestru + „Brak aktywnego źródła”. */
	buckets: PriceSourceBucket[];
	currentSalesNetPln: number;
	/**
	 * Korekty partnerskie okresu bieżącego (suma po kaflach). Równe
	 * `partnerCorrectionsPln` z `/segments` przy tych samych filtrach.
	 */
	partnerCorrectionsPln: number;
	partnerCorrectionsNote: string;
	/** Rozbicie per segment w kolejności `/segments`: Kosmetyki, RX100 — NOREF, OTC. */
	segmentBlocks: PriceSourceSegmentBlock[];
	segmentBlocksNote: string;
	/** JEDYNE zdanie pokazywane na ekranie; reszta not idzie do `Kryteriów`. */
	screenNote: string;
	howWeAssignTitle: string;
	howWeAssignNote: string;
	/** Semantyka: BIEŻĄCE pokrycie, nie historyczne źródło ceny transakcji. */
	coverageNote: string;
	resolverNote: string;
	/** Dlaczego nie ma bucketów „Krótka data” i „Marża degresywna”. */
	missingBucketsNote: string;
	sourcesNote: string;
	/** Czego ta sekcja NIE obejmuje — sprzedaż bez kodu BLOZ (jest w Przeglądzie). */
	noBlozNote: string;
	/** Liczba klas równoważności aptek = liczba wywołań resolvera (diagnostyka). */
	pharmacyClassCount: number;
	meta: ResponseMeta;
}

function buildQS(params: DashboardQuery): string {
	const qs = new URLSearchParams();
	if (params.scope) qs.set("scope", params.scope);
	if (params.excludedIdfirms) qs.set("excludedIdfirms", params.excludedIdfirms);
	if (params.category) qs.set("category", params.category);
	if (params.kosmetyk) qs.set("kosmetyk", "true");
	if (params.natubay) qs.set("natubay", "true");
	if (params.rodzaj && params.rodzaj !== "ALL") qs.set("rodzaj", params.rodzaj);
	if (params.dateFrom) qs.set("dateFrom", params.dateFrom);
	if (params.dateTo) qs.set("dateTo", params.dateTo);
	const s = qs.toString();
	return s ? `?${s}` : "";
}

/**
 * Przegląd — 4 KPI, pełny szereg dzienny okresu bieżącego oraz totale i
 * szeregi okresów porównawczych. JEDNA odpowiedź zasila kafle, wykres i cały
 * eksport XLSX — dzięki temu plik nie może pochodzić z dwóch snapshotów.
 */
export function fetchDashboardOverview(
	params: DashboardQuery,
	signal?: AbortSignal,
): Promise<DashboardOverviewResponse> {
	return apiFetch<DashboardOverviewResponse>(`/reports/dashboard/overview${buildQS(params)}`, {
		signal,
	});
}

/**
 * Porównanie w ramach zakresu — ta sama populacja co Przegląd, rozbita po
 * jednym wymiarze organizacyjnym. JEDNA odpowiedź zasila tabelę i XLSX.
 */
export function fetchDashboardScopeBreakdown(
	params: DashboardQuery,
	by: BreakdownDimension | undefined,
	signal?: AbortSignal,
): Promise<DashboardScopeBreakdownResponse> {
	const qs = buildQS(params);
	const extra = by ? `${qs ? "&" : "?"}by=${by}` : "";
	return apiFetch<DashboardScopeBreakdownResponse>(
		`/reports/dashboard/scope-breakdown${qs}${extra}`,
		{ signal },
	);
}

/**
 * Segmenty — macierz `segment × dostępny okres`, udziały i delty. JEDNA
 * odpowiedź zasila kafle, oba wykresy i cały eksport XLSX.
 */
/**
 * Trend 12 miesięcy. Zakres dat z paska filtrów NIE jedzie do API — okno
 * ustala serwer (kroczące 12 miesięcy do D-1). Wysłanie dat byłoby myląceniem
 * cache: dwie różne odpowiedzi dla tych samych danych.
 */
export function fetchDashboardTrend(
	params: DashboardQuery,
	signal?: AbortSignal,
): Promise<DashboardTrendResponse> {
	const { dateFrom: _f, dateTo: _t, ...rest } = params;
	return apiFetch<DashboardTrendResponse>(`/reports/dashboard/trend${buildQS(rest)}`, { signal });
}

export function fetchDashboardSegments(
	params: DashboardQuery,
	signal?: AbortSignal,
): Promise<DashboardSegmentsResponse> {
	return apiFetch<DashboardSegmentsResponse>(`/reports/dashboard/segments${buildQS(params)}`, {
		signal,
	});
}

/**
 * Źródła ceny — macierz `źródło × dostępny okres`, udziały i delty. JEDNA
 * odpowiedź zasila kafle, oba wykresy i cały eksport XLSX.
 */
export function fetchDashboardPriceSource(
	params: DashboardQuery,
	signal?: AbortSignal,
): Promise<DashboardPriceSourceResponse> {
	return apiFetch<DashboardPriceSourceResponse>(
		`/reports/dashboard/price-source${buildQS(params)}`,
		{ signal },
	);
}

/** QS sekcji Kategorie/Towary: wspólne filtry + CSV multiselectu kategorii. */
function buildCategoryProductsQS(params: DashboardQuery, categories: string[]): string {
	const qs = new URLSearchParams(buildQS(params).replace(/^\?/, ""));
	if (categories.length > 0) qs.set("categories", categories.join(","));
	const s = qs.toString();
	return s ? `?${s}` : "";
}

/**
 * Kolumny listy SKU, po których wolno sortować — lustro whitelisty backendu
 * (`sort_column` w `category_products.rs`). Kolumny kartotekowe (producent,
 * dawka) nie sortują, bo backend dokłada `public.bloz` dopiero po `LIMIT`.
 */
export const SKU_SORT_KEYS = [
	"name",
	"bloz07",
	"qty",
	"salesNetPln",
	"marginPln",
	"marginPct",
	"categorySharePct",
	"stockQty",
	"stockValueNet",
] as const;

/** Główna miara listy SKU (URL `skuMeasure`); zmienia kolumnę z mikrobarem i podsumowanie. */
export const SKU_MEASURES = ["sales", "margin", "stock"] as const;
export type SkuMeasure = (typeof SKU_MEASURES)[number];
export const SKU_DEFAULT_MEASURE: SkuMeasure = "sales";
export type SkuSortKey = (typeof SKU_SORT_KEYS)[number];
export type SkuSortDir = "asc" | "desc";
export interface SkuSort {
	key: SkuSortKey;
	dir: SkuSortDir;
}

/** Domyślna kolejność ekranu = ta sama, którą backend daje bez parametrów. */
export const SKU_DEFAULT_SORT: SkuSort = { key: "salesNetPln", dir: "desc" };

/**
 * Wyszukiwanie i sortowanie listy SKU. Oba filtrują i porządkują POPULACJĘ po
 * stronie serwera — sortowanie klienckie objęłoby tylko załadowane strony,
 * więc pierwsza pozycja „najtańszych” byłaby najtańsza ze stu wierszy, a nie
 * z czterdziestu tysięcy.
 */
export const CURRENT_PRICELIST_SOURCES = [
	"TOP_CENNIK_1",
	"TOP_CENNIK_2",
	"TOP_CENNIK_3",
	"LIDER",
] as const;
export type CurrentPricelistSource = (typeof CURRENT_PRICELIST_SOURCES)[number];
export const currentPricelistLabel = (code: string) =>
	code === "LIDER" ? "Lider" : `TOP ${code.slice(-1)}`;
export const currentPricelistNote = (sources: readonly string[], day: string) =>
	`Bieżące przypisanie cennika w wybranym zakresie: ${sources.map(currentPricelistLabel).join(", ")} — stan na ${day}. Co najmniej jedna uprawniona apteka; bez dziedziczenia spoza zakresu. Sprzedaż dotyczy wybranego okresu, kwoty obejmują cały uprawniony zakres. To filtr przypisań, nie wszystkich efektywnych cen.`;

export const currentContractNote = (day: string) =>
	`Kontrakt — aktywny dziś: ${day} (dzień kwalifikacji serwera). Bieżące przypisanie produktu w co najmniej jednej uprawnionej aptece wybranego zakresu, niezależne od okresu sprzedaży. Miary obejmują cały wybrany zakres.`;

export interface SkuListOptions {
	/** Fraza szukana w nazwie albo kodzie BLOZ (podciąg, bez wielkości liter). */
	q?: string;
	sort?: SkuSort;
	/** Serializowane `columnFilters` kartoteki `public.bloz`. */
	columnFilters?: string;
	/**
	 * Kolumny magazynowe ze snapshotu — WYŁĄCZNIE na żądanie (miara Magazyn,
	 * szczegół wiersza, eksport). Lista z filtrem cennika również je pobiera, aby wersja obejmowała późniejszy eksport.
	 */
	withStock?: boolean;
	/** Dzień snapshotu z pierwszej strony; kolejne strony i eksport są do niego przypięte. */
	stockAsOf?: string | null;
	/**
	 * Dokładne kody BLOZ — zawężenie TEJ SAMEJ populacji (scope, okres,
	 * segment, kategorie, fraza, filtry) do podanych towarów. Zaznaczenie
	 * spoza wczytanych stron, eksport zaznaczonych i magazyn jednego wiersza
	 * schodzą nim jednym bounded strzałem, zamiast pełnym eksportem albo
	 * frazą-podciągiem. Backend odrzuca listę dłuższą niż `SKU_BLOZ_MAX`.
	 */
	bloz?: readonly string[];
	/**
	 * Kody atrybutów (liście gałęzi „30 ATRYBUT” drzewa kategorii) — towar
	 * musi mieć KTÓRYKOLWIEK z nich jako potwierdzone przypisanie (to samo
	 * źródło co kolumna „Atrybuty (potwierdzone)”). Serwerowo, przed
	 * stronicowaniem: strona, licznik, mianownik i eksport widzą jeden zbiór.
	 */
	attrs?: readonly string[];
	sources?: readonly CurrentPricelistSource[];
	contract?: boolean;
	pricelistAsOf?: string | null;
	pricelistVersion?: string | null;
	/** Populacja listy; brak = `sales` (patrz `SkuUniverse`). */
	universe?: SkuUniverse;
}

/** Lustro `PRODUCTS_BLOZ_MAX` backendu — większe zaznaczenie to „Eksportuj wszystkie”. */
export const SKU_BLOZ_MAX = 2000;
export const PRODUCTS_BLOZ_INVALID_CODE = "PRODUCTS_BLOZ_INVALID";
/** Lustro `PRODUCTS_ATTR_MAX` backendu. */
export const SKU_ATTRS_MAX = 20;
/** Prefiks gałęzi atrybutów w drzewie kategorii (`30 ATRYBUT`). */
export const ATTRIBUTE_BRANCH_CODE = "30";

/**
 * Dokleja `q`/`bloz`/`sort`/`dir`/`withStock`/`stockAsOf` do QS. Domyślna kolejność nie dokłada nic.
 *
 * `bloz` pominięte (`undefined`) = bez zawężenia („Eksportuj wszystkie”, zwykła
 * lista). `bloz: []` to błąd wywołania, nie „wszystkie”: pusta tablica
 * oznacza, że ktoś prosi o zaznaczenie, którego nie ma — cichy brak parametru
 * oddałby pełną populację jako eksport zaznaczonych. Backend odrzuca to samo
 * (`bloz=` → 422), więc strażnik tylko zatrzymuje strzał przed siecią.
 */
function applySkuListOptions(qs: URLSearchParams, options?: SkuListOptions): URLSearchParams {
	const q = options?.q?.trim();
	if (q) qs.set("q", q);
	if (options?.bloz) {
		if (options.bloz.length === 0) {
			throw new Error("Puste zaznaczenie BLOZ — pomiń `bloz`, żeby pobrać całą listę.");
		}
		qs.set("bloz", options.bloz.join(","));
	}
	if (options?.sources?.length) {
		qs.set("sources", options.sources.join(","));
	}
	if (options?.contract) qs.set("contract", "true");
	if (options?.sources?.length || options?.contract) {
		if (options.pricelistAsOf) qs.set("pricelistAsOf", options.pricelistAsOf);
		if (options.pricelistVersion) qs.set("pricelistVersion", options.pricelistVersion);
	}
	if (options?.attrs && options.attrs.length > 0) qs.set("attrs", options.attrs.join(","));
	if (options?.columnFilters) qs.set("columnFilters", options.columnFilters);
	const sort = options?.sort;
	if (sort && !(sort.key === SKU_DEFAULT_SORT.key && sort.dir === SKU_DEFAULT_SORT.dir)) {
		qs.set("sort", sort.key);
		qs.set("dir", sort.dir);
	}
	if (options?.withStock) {
		qs.set("withStock", "true");
		if (options.stockAsOf) qs.set("stockAsOf", options.stockAsOf);
	}
	// `sales` = domyślne; nie dokładamy, żeby dotychczasowe żądania (i fixture
	// bajtowy suity `bloz_gate`) zostały bajt w bajt takie same.
	if (options?.universe && options.universe !== "sales") qs.set("universe", options.universe);
	return qs;
}

export function fetchDashboardCategoryProducts(
	params: DashboardQuery,
	categories: string[],
	signal?: AbortSignal,
): Promise<DashboardCategoryProductsResponse> {
	return apiFetch<DashboardCategoryProductsResponse>(
		`/reports/dashboard/category-products${buildCategoryProductsQS(params, categories)}`,
		{ signal },
	);
}

/**
 * Drzewo kategorii (rodzic → dzieci) z KPI per okres. JEDNA odpowiedź niesie
 * wszystkie węzły z danymi — rozwijanie gałęzi jest czysto klienckie, bez
 * refetchu per klik; ta sama odpowiedź zasila kafle, listę i eksport XLSX.
 */
export function fetchDashboardCategoryHierarchy(
	params: DashboardQuery,
	categories: string[],
	signal?: AbortSignal,
): Promise<DashboardCategoryHierarchyResponse> {
	return apiFetch<DashboardCategoryHierarchyResponse>(
		`/reports/dashboard/category-hierarchy${buildCategoryProductsQS(params, categories)}`,
		{ signal },
	);
}

/**
 * Szereg dzienny + totale kategorii otwartych w szczególe.
 * `detail` to SELEKCJA wierszy rankingu (dokładne kody + `__none`), a nie
 * drugi filtr populacji — populacja to nadal `categories` ∩ segment ∩ scope
 * ∩ okres, dlatego totale równają się wierszom rankingu.
 */
export function fetchDashboardCategoryDetail(
	params: DashboardQuery,
	categories: string[],
	detail: string[],
	grain: DetailGrain = "day",
	signal?: AbortSignal,
): Promise<DashboardCategoryDetailResponse> {
	const qs = new URLSearchParams(buildCategoryProductsQS(params, categories).replace(/^\?/, ""));
	if (detail.length > 0) qs.set("detail", detail.join(","));
	// Brak parametru = dzień (stary kontrakt bez zmian w URL-u).
	if (grain === "month") qs.set("grain", "month");
	const s = qs.toString();
	return apiFetch<DashboardCategoryDetailResponse>(
		`/reports/dashboard/category-detail${s ? `?${s}` : ""}`,
		{ signal },
	);
}

/** Jedna grupa zarobku w jednej kategorii; tylko grupy z danymi (brak ≠ 0). */
export interface CategorySegmentCell extends OverviewTotals {
	segment: SegmentToken;
	priority: number;
	label: string;
	/** Udział w sprzedaży netto KATEGORII; null = mianownik ≤ 0. */
	sharePct: number | null;
}

export interface CategorySegmentsRow {
	/** null ⇒ „Bez kategorii”. */
	categoryCode: string | null;
	categoryLabel: string | null;
	/** Suma grup = wiersz rankingu tej kategorii przy tych samych filtrach. */
	totals: OverviewTotals;
	/** W kolejności priorytetu grup. */
	segments: CategorySegmentCell[];
}

export interface DashboardCategorySegmentsResponse {
	definitions: { segment: SegmentToken; priority: number; label: string; definition: string }[];
	categories: CategorySegmentsRow[];
	priorityNote: string;
	natubayNote: string;
	noBlozNote: string;
	correctionsNote: string;
	meta: ResponseMeta;
}

/**
 * Rozbicie kategorii otwartych w szczególe na grupy zarobku Segmentów
 * (rozłączne, sumują się do wiersza rankingu). Populacja jak `/category-detail`:
 * `detail` wybiera kategorie, `categories` ∩ segment ∩ scope ∩ okres ją
 * wyznaczają. Wołane DOPIERO po rozwinięciu szczegółu.
 */
export function fetchDashboardCategorySegments(
	params: DashboardQuery,
	categories: string[],
	detail: string[],
	signal?: AbortSignal,
): Promise<DashboardCategorySegmentsResponse> {
	const qs = new URLSearchParams(buildCategoryProductsQS(params, categories).replace(/^\?/, ""));
	if (detail.length > 0) qs.set("detail", detail.join(","));
	const s = qs.toString();
	return apiFetch<DashboardCategorySegmentsResponse>(
		`/reports/dashboard/category-segments${s ? `?${s}` : ""}`,
		{ signal },
	);
}

/**
 * Jedna strona listy SKU — źródło zakładki „Pełna lista / analiza SKU”.
 * Ekran NIGDY nie ciągnie całego zbioru (~40 tys. BLOZ dla 90 dni i całej
 * sieci); pełny zbiór idzie wyłącznie przez endpoint eksportowy, na jawne
 * kliknięcie „Eksportuj wszystkie”.
 */
export function fetchDashboardCategoryProductsPage(
	params: DashboardQuery,
	categories: string[],
	page: { limit: number; offset: number },
	signal?: AbortSignal,
	options?: SkuListOptions,
): Promise<DashboardCategoryProductsPageResponse> {
	const qs = new URLSearchParams(buildCategoryProductsQS(params, categories).replace(/^\?/, ""));
	qs.set("limit", String(page.limit));
	qs.set("offset", String(page.offset));
	applySkuListOptions(qs, options);
	return apiFetch<DashboardCategoryProductsPageResponse>(
		`/reports/dashboard/category-products/page?${qs.toString()}`,
		signal ? { signal } : undefined,
	);
}

/**
 * Pełny filtrowany zbiór backendowy (wszystkie BLOZ-y, nie TOP 10).
 *
 * `options` niesie wyszukiwanie i sortowanie listy SKU, więc „Eksportuj
 * wszystkie” oddaje dokładnie tę populację i kolejność, którą widać na
 * ekranie. Arkusz `Kryteria` wypisuje jedno i drugie.
 */
export function fetchDashboardCategoryProductsExport(
	params: DashboardQuery,
	categories: string[],
	signal?: AbortSignal,
	options?: SkuListOptions,
): Promise<DashboardCategoryProductsExportResponse> {
	const qs = applySkuListOptions(
		new URLSearchParams(buildCategoryProductsQS(params, categories).replace(/^\?/, "")),
		options,
	);
	const s = qs.toString();
	return apiFetch<DashboardCategoryProductsExportResponse>(
		`/reports/dashboard/category-products/export${s ? `?${s}` : ""}`,
		signal ? { signal } : undefined,
	);
}

/**
 * Dzienny szczegół JEDNEGO SKU — wołany dopiero po rozwinięciu wiersza.
 * Te same filtry populacji co lista (scope, okres, segment, kategorie);
 * fraza i filtry kartoteki nie jadą, bo dotyczą cech towaru, który już
 * przez nie przeszedł.
 */
export function fetchDashboardSkuDaily(
	params: DashboardQuery,
	categories: string[],
	bloz07: string,
	signal?: AbortSignal,
	list?: SkuListOptions,
): Promise<DashboardSkuDailyResponse> {
	const qs = new URLSearchParams(buildCategoryProductsQS(params, categories).replace(/^\?/, ""));
	qs.set("bloz07", bloz07);
	applySkuListOptions(qs, list);
	return apiFetch<DashboardSkuDailyResponse>(
		`/reports/dashboard/category-products/daily?${qs.toString()}`,
		signal ? { signal } : undefined,
	);
}

/**
 * Bez `limit` odpowiedź zostaje domyślnym TOP 10 ekranu — parametr wysyła
 * WYŁĄCZNIE eksport pełnego zbioru, więc cache widoku się nie zmienia.
 */
export function fetchDashboardCentralPricing(
	params: DashboardQuery,
	signal?: AbortSignal,
	limit?: number,
): Promise<DashboardCentralPricingResponse> {
	const qs = new URLSearchParams(buildQS(params).replace(/^\?/, ""));
	if (limit != null) qs.set("limit", String(limit));
	const s = qs.toString();
	return apiFetch<DashboardCentralPricingResponse>(
		`/reports/dashboard/central-pricing${s ? `?${s}` : ""}`,
		{ signal },
	);
}

/** Bez `limit` odpowiedź zostaje domyślnym TOP 10 panelu — patrz `fetchDashboardCentralPricing`. */
export function fetchDashboardCennikAnomalies(
	params: DashboardQuery,
	cennikName: string,
	signal?: AbortSignal,
	limit?: number,
): Promise<DashboardCennikAnomaliesResponse> {
	const qs = new URLSearchParams(buildQS(params).replace(/^\?/, ""));
	qs.set("cennikName", cennikName);
	if (limit != null) qs.set("limit", String(limit));
	return apiFetch<DashboardCennikAnomaliesResponse>(
		`/reports/dashboard/central-pricing/anomalies?${qs.toString()}`,
		{ signal },
	);
}

export function fetchDashboardCheapestBasket(
	params: DashboardQuery,
	signal?: AbortSignal,
): Promise<DashboardCheapestBasketResponse> {
	return apiFetch<DashboardCheapestBasketResponse>(
		`/reports/dashboard/cheapest-basket${buildQS(params)}`,
		{ signal },
	);
}

/**
 * KPI magazynu. Zakres dat dashboardu NIE jest wysyłany — stan to bieżący
 * snapshot, a okno rotacji jest stałe (90 dni) i ustala je backend; wysyłanie
 * `dateFrom/dateTo` sugerowałoby, że okres cofa stan.
 */
export function fetchDashboardStockKpi(
	params: DashboardQuery,
	signal?: AbortSignal,
): Promise<DashboardStockKpiResponse> {
	const qs = new URLSearchParams();
	if (params.scope) qs.set("scope", params.scope);
	if (params.excludedIdfirms) qs.set("excludedIdfirms", params.excludedIdfirms);
	if (params.category) qs.set("category", params.category);
	if (params.kosmetyk) qs.set("kosmetyk", "true");
	if (params.natubay) qs.set("natubay", "true");
	if (params.rodzaj && params.rodzaj !== "ALL") qs.set("rodzaj", params.rodzaj);
	const s = qs.toString();
	return apiFetch<DashboardStockKpiResponse>(`/reports/dashboard/stock-kpi${s ? `?${s}` : ""}`, {
		signal,
	});
}

export function fetchDashboardForecast(
	params: DashboardQuery,
	signal?: AbortSignal,
): Promise<DashboardForecastResponse> {
	return apiFetch<DashboardForecastResponse>(`/reports/dashboard/forecast${buildQS(params)}`, {
		signal,
	});
}

// ---------------------------------------------------------------------------
// Ryzyko krótkiej daty (FEFO)
// ---------------------------------------------------------------------------

/** Lustro `HORIZONS` z `expiry_risk.rs` — spoza listy API zwraca 400. */
export const EXPIRY_HORIZONS = [90, 180, 365] as const;
export type ExpiryHorizon = (typeof EXPIRY_HORIZONS)[number];
export const DEFAULT_EXPIRY_HORIZON: ExpiryHorizon = 180;
export const EXPIRY_RISK_DEFAULT_LIMIT = 25;
/** Sufit `limit` backendu — pełny zbiór do XLSX jednym strzałem (jak `stock-detail`). */
export const EXPIRY_RISK_EXPORT_LIMIT = 50_000;

export function isExpiryHorizon(value: unknown): value is ExpiryHorizon {
	return EXPIRY_HORIZONS.includes(value as ExpiryHorizon);
}

export interface ExpiryRiskKpi {
	/** FAKT: wartość zakupu netto partii z terminem w horyzoncie. */
	valueInHorizonNet: number;
	packsInHorizon: number;
	/** PROGNOZA (FEFO): wartość, która prawdopodobnie zostanie na termin. */
	riskValueNet: number;
	riskPacks: number;
	blozAtRiskCount: number;
	blozInHorizonCount: number;
	pharmacyCount: number;
	positionCount: number;
	batchCount: number;
	/** Coverage gap: partie na stanie bez daty ważności (poza modelem). */
	undatedBatchCount: number;
	undatedValueNet: number;
	/** Poza modelem: partie z sentinelem `0000000` (towar bez BLOZ), liczone osobno od „bez daty”. */
	noBlozBatchCount: number;
	noBlozValueNet: number;
}

export interface ExpiryRiskRow {
	bloz07: string;
	name: string | null;
	opakowanie: string | null;
	dawka: string | null;
	nearestDatwz: string;
	/** Dni od snapshotu; ujemne = już po terminie. */
	nearestDays: number;
	packsInHorizon: number;
	valueInHorizonNet: number;
	/** Surowa sprzedaż 90 dni (szt.), bywa ≤ 0 — wtedy tempo = 0. */
	salesQty90d: number;
	/** false = któraś apteka z partią nie ma żadnego rekordu sprzedaży kodu w oknie (np. kod lokalny `T…`); tempo 0, cała partia w ryzyku. */
	salesKnown: boolean;
	riskPacks: number;
	riskValueNet: number;
	pharmacyCount: number;
	batchCount: number;
	/** Atrybut 3003 (zalegacze własne) — kwalifikacja do Wyników TOP. */
	zalegaczWlasne: boolean;
	/** Cena z aktywnego cennika centralnego (read-only); null = brak. */
	centralPricePln: number | null;
}

export interface DashboardExpiryRiskResponse {
	horizonDays: ExpiryHorizon;
	/** snapshot + horyzont — `datwzTo` deep-linku do raportu dat ważności. */
	horizonTo: string;
	kpi: ExpiryRiskKpi;
	rows: ExpiryRiskRow[];
	/** Wszystkie BLOZ w horyzoncie — UI mówi „N z total”. */
	totalRows: number;
	limit: number;
	offset: number;
	/** `offset + rows.length < totalRows` — zbiór nie zmieścił się w odpowiedzi. */
	truncated: boolean;
	salesWindow: SalesWindow;
	meta: ResponseMeta;
}

export interface ExpiryRiskParams extends DashboardQuery {
	horizon: ExpiryHorizon;
	limit?: number;
	offset?: number;
	/** Dzień snapshotu z kafla; rozjazd z bieżącym = 422 `STOCK_SNAPSHOT_STALE`. */
	dataAsOf?: string;
}

export function fetchDashboardExpiryRisk(
	params: ExpiryRiskParams,
	signal?: AbortSignal,
): Promise<DashboardExpiryRiskResponse> {
	const qs = new URLSearchParams();
	if (params.scope) qs.set("scope", params.scope);
	if (params.excludedIdfirms) qs.set("excludedIdfirms", params.excludedIdfirms);
	if (params.category) qs.set("category", params.category);
	if (params.kosmetyk) qs.set("kosmetyk", "true");
	if (params.natubay) qs.set("natubay", "true");
	if (params.rodzaj && params.rodzaj !== "ALL") qs.set("rodzaj", params.rodzaj);
	qs.set("horizon", String(params.horizon));
	qs.set("limit", String(params.limit ?? EXPIRY_RISK_DEFAULT_LIMIT));
	if (params.offset) qs.set("offset", String(params.offset));
	if (params.dataAsOf) qs.set("dataAsOf", params.dataAsOf);
	return apiFetch<DashboardExpiryRiskResponse>(`/reports/dashboard/expiry-risk?${qs.toString()}`, {
		signal,
	});
}
